import React from 'react'
import { Skeleton, Flex, Divider, Box, Icon } from '@chakra-ui/react'
import { navigate } from 'gatsby'
import { LuExternalLink, LuMinus, LuX } from 'react-icons/lu'

import TeamChatUsers from './manage-users'
import { useClinic } from '~contexts/clinic-context'
import { useTeamChatConversation } from '../provider'
import SharedStack from '~components/shared/wrappers/shared-stack'
import { InternalChatTitle } from '~components/team-chat/layout/sidebar/chat-item'
import ClinicTag from '~components/chats/chats-list/chat-preview/clinic-tag'
import IconButtonWithTooltip from '~components/shared/icon-button-with-tooltip'
import { useOpenTeamChats } from '~components/team-chat/popovers/open-team-chats-provider'
import { UserRoutes } from '~config/routes'

interface Props {
    compact?: boolean
    onClose?: () => void
    onMinimize?: () => void
}

export default function TeamChatHeader({
    compact = false,
    onClose,
    onMinimize,
}: Props) {
    const { clinicGroup } = useClinic()
    const { internalChat, internalChatUsers, loading } =
        useTeamChatConversation()
    const { openTeamChat } = useOpenTeamChats()

    function handlePopOut() {
        if (!internalChat?.id) return
        openTeamChat(internalChat.id)
        navigate(UserRoutes.TeamChat)
    }

    function handleOpenFullPage() {
        if (!internalChat?.id) return
        onClose?.()
        navigate(UserRoutes.TeamChatWithId(internalChat.id))
    }

    if (compact) {
        return (
            <Box w="full" flexShrink={0} borderBottomWidth={1} borderBottomColor="gray.200">
                <Flex pt={2} pb={1} pl={8} pr={3} align="center" gap={2}>
                    {loading ? (
                        <Skeleton h="20px" w="140px" rounded="md" />
                    ) : (
                        <Box flex={1} minW={0} overflow="hidden">
                            <InternalChatTitle
                                chat={internalChat}
                                users={internalChatUsers}
                                labelOverrides={{
                                    fontSize: 'sm',
                                    fontWeight: 'semibold',
                                    mb: 0,
                                }}
                            />
                        </Box>
                    )}
                    <SharedStack row fit spacing={1}>
                        <IconButtonWithTooltip
                            icon={<Icon as={LuExternalLink} fontSize="sm" />}
                            label="Open in Team Chats"
                            onClick={handleOpenFullPage}
                            size="xs"
                            rounded="full"
                            variant="ghost"
                            h="28px"
                            w="28px"
                        />
                        {onMinimize && (
                            <IconButtonWithTooltip
                                icon={<Icon as={LuMinus} fontSize="md" />}
                                label="Minimize"
                                onClick={onMinimize}
                                size="xs"
                                rounded="full"
                                variant="ghost"
                                h="28px"
                                w="28px"
                            />
                        )}
                        {onClose && (
                            <IconButtonWithTooltip
                                icon={<Icon as={LuX} fontSize="md" />}
                                label="Close"
                                onClick={onClose}
                                size="xs"
                                rounded="full"
                                variant="ghost"
                                h="28px"
                                w="28px"
                            />
                        )}
                    </SharedStack>
                </Flex>
                {!loading && (
                    <Flex pb={2} px={3} align="center" justify="space-between">
                        {!!clinicGroup && !!internalChat.clinic && (
                            <ClinicTag clinic={internalChat.clinic} />
                        )}
                        <Box ml="auto">
                            <TeamChatUsers />
                        </Box>
                    </Flex>
                )}
            </Box>
        )
    }

    return (
        <>
            <Flex
                justifyContent="space-between"
                align="center"
                w="full"
                px={5}
                py={3}
            >
                {loading ? (
                    <>
                        <Skeleton h="36px" w="196px" rounded="lg" />
                        <Skeleton h="36px" w="68px" rounded="lg" />
                    </>
                ) : (
                    <>
                        <SharedStack row fit spacing={4}>
                            <InternalChatTitle
                                chat={internalChat}
                                users={internalChatUsers}
                                labelOverrides={{
                                    fontSize: 'lg',
                                    fontWeight: 'bold',
                                }}
                            />
                            {!!clinicGroup && !!internalChat.clinic && (
                                <ClinicTag clinic={internalChat.clinic} />
                            )}
                        </SharedStack>
                        <SharedStack row fit spacing={2} align="center">
                            <IconButtonWithTooltip
                                icon={
                                    <Icon as={LuExternalLink} fontSize="sm" />
                                }
                                label="Pop out chat"
                                onClick={handlePopOut}
                                size="sm"
                                rounded="full"
                                variant="ghost"
                            />
                            <TeamChatUsers />
                        </SharedStack>
                    </>
                )}
            </Flex>
            <Divider />
        </>
    )
}
